import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import React, { useMemo } from "react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useToastContext } from "../../../../contexts/ToastContext/ToastContext";
import localeText from "../../../../locale";
import { formatDate } from "../../../../utilities/formatDate";
import handleCurrency from "../../../../utilities/CurrencySwitch";
import OrdersTable from "./components/OrdersTable";
import RatingModal from "./components/RatingModal";
import PendingReceiptReminder from "./components/PendingReceiptReminder";
import useOrders from "./hooks/useOrders";
import DashboardSummaryStrip from "../../components/DashboardSummaryStrip/DashboardSummaryStrip";
import "../../pages/FinancialHub/components/FinancialTransactionsTable/FinancialTransactionsTable.css";

const Orders = (props) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const {
    isLoading: loading,
    orders,
    confirmReceivedVisible,
    currentOrder,
    parcelInfo,
    confirmSendVisible,
    displayRateVisible,
    ratingSeller,
    ratingProduct,
    comment,
    commentProductService,
    shippingCompany,
    trackingObject,
    displayRegisterPickupVisible,
    storeName,
    filterByStoreName,
    setStoreName,
    setRatingSeller,
    setRatingProduct,
    setConfirmSendVisible,
    setOrderId_,
    setConfirmReceivedVisible,
    setDisplayRateVisible,
    setComment,
    setCommentProductService,
    setCurrency,
    setShippingCompany,
    setTrackingObject,
    setDisplayRegisterPickupVisible,
    registerPickup,
    orderRate,
    sendOrder,
    receiveOrder,
    setCurrentOrder,
  } = useOrders({
    sales: props.sales,
    admin: props.admin,
    onErrorLoadAdminOrders: (code) => showErrorToast({ code }),
    onErrorLoadUserOrders: (code) => showErrorToast({ code }),
    onErrorReceiveOrder: (code) => showErrorToast({ code }),
    onErrorSendOrder: (code) => showErrorToast({ code }),
    onErrorFillOutAll: () => showErrorToast({ detail: "pleaseFillOutAll" }),
    onErrorDownloadProducts: (code) => showErrorToast({ code }),
    onErrorGetParcel: (code) => showErrorToast({ code }),
    onErrorProvideRating: () => showErrorToast({ detail: "pleaseProvideRating" }),
    onErrorOrderRate: (code) => showErrorToast({ code }),
    onErrorValidatePickup: () => showErrorToast({ detail: "dateMustNotBeNull" }),
    onErrorRegisterPickup: (code) => showErrorToast({ code }),
    onSuccessSendOrder: () => showSuccessToast({ detail: "productSentSuccessfully" }),
    onSuccessReceiveOrder: () => showSuccessToast({ detail: "productReceivedSuccessfully" }),
    onSuccessOrderRate: () => showSuccessToast({ detail: "orderRatedSuccessfully" }),
  });

  const isPurchasesView = !props.admin && !props.sales;

  const purchasesSummary = useMemo(() => {
    if (!isPurchasesView || !Array.isArray(orders) || orders.length === 0) {
      return { total: 0, lastDateLabel: localeText("noPurchases") || "Nenhuma compra ainda" };
    }
    const total = orders.length;
    const lastDate = orders
      .map((o) => new Date(o.date))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => b.getTime() - a.getTime())[0];
    const lastDateLabel = lastDate
      ? formatDate(lastDate)
      : localeText("noPurchases") || "Nenhuma compra ainda";
    return { total, lastDateLabel };
  }, [isPurchasesView, orders]);

  if (props.sales && props.plan === "Promoter") {
    return (
      <DashboardLayout>
        <div className="card border-1 border-300">
          <div className="text-center">
            <i className="pi pi-lock text-8xl text-400 my-4" />
            <h2 className="my-6">{localeText("planNotAllowed")}</h2>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="flex align-items-center gap-4 mb-4 mt-2">
        <div
          className="border-circle p-3"
          style={{
            border: "1px solid var(--theme-border, #EBEBEB)",
            width: "54px",
            height: "54px",
          }}
        >
          <i
            className="pi pi-shopping-cart text-xl"
            style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
          />
        </div>
        <div>
          <h1 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
            {isPurchasesView ? localeText("myPurchases") || "Minhas compras" : localeText("orders")}
          </h1>
          <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
            {isPurchasesView
              ? localeText("myPurchasesSubtitle") ||
                "Acompanhe suas compras e o status de cada pedido."
              : localeText("ordersSubtitle") ||
                "Gerencie pedidos e acompanhe o status das entregas."}
          </p>
        </div>
      </div>
      {isPurchasesView && (
        <DashboardSummaryStrip
          items={[
            {
              label: localeText("totalPurchases") || "Total de compras",
              value: purchasesSummary.total,
            },
            {
              label: localeText("lastPurchaseDate") || "Última compra",
              value: purchasesSummary.lastDateLabel,
            },
          ]}
        />
      )}

      <div>
        <RatingModal
          comment={comment}
          commentProductService={commentProductService}
          setCommentProductService={setCommentProductService}
          displayRateVisible={displayRateVisible}
          loading={loading}
          orderRate={orderRate}
          ratingProduct={ratingProduct}
          ratingSeller={ratingSeller}
          setComment={setComment}
          setDisplayRateVisible={setDisplayRateVisible}
          setRatingProduct={setRatingProduct}
          setRatingSeller={setRatingSeller}
        />

        <ConfirmDialog
          visible={confirmReceivedVisible}
          onHide={() => setConfirmReceivedVisible(false)}
          message={() => {
            return (
              <>
                {loading ? (
                  <div className="text-center">
                    <i className="pi pi-spin pi-spinner text-8xl text-500 my-6"></i>
                  </div>
                ) : (
                  <>
                    {currentOrder && !currentOrder.itens[0].product.downloadable ? (
                      <>{localeText("receiveThisOrder")}</>
                    ) : (
                      <>{localeText("receiveDownloadLinksConfirmation")}</>
                    )}
                  </>
                )}
              </>
            );
          }}
          header={localeText("confirmYourAction")}
          icon="pi pi-exclamation-triangle"
          accept={() => receiveOrder(currentOrder.buyer.username, currentOrder.id)}
          reject={() => setConfirmReceivedVisible(false)}
          acceptLabel={localeText("confirm")}
          rejectLabel={localeText("cancel")}
        />

        <ConfirmDialog
          visible={displayRegisterPickupVisible}
          onHide={() => setDisplayRegisterPickupVisible(false)}
          message={() => {
            return <>{localeText("registerPickupConfirmation")}</>;
          }}
          header={localeText("confirmYourAction")}
          icon="pi pi-exclamation-triangle"
          accept={() => registerPickup()}
          reject={() => setDisplayRegisterPickupVisible(false)}
          acceptLabel={localeText("confirm")}
          rejectLabel={localeText("cancel")}
        />

        <ConfirmDialog
          visible={confirmSendVisible}
          onHide={() => setConfirmSendVisible(false)}
          message={() => {
            return (
              <>
                {loading ? (
                  <div className="text-center">
                    <i className="pi pi-spin pi-spinner text-8xl text-500 my-6"></i>
                  </div>
                ) : (
                  <>
                    {currentOrder?.itens[0]?.product?.subCategory?.service ? (
                      <p>{localeText("confirmOrderWasSent2")}</p>
                    ) : (
                      <>
                        {localeText("confirmOrderWasSent")}
                        <ul className="list-none mx-0 mt-4 p-0">
                          <li>
                            <label htmlFor="shipping">{localeText("shippingCompany")}:</label>
                          </li>
                          <li>
                            <InputText
                              className="mt-2 w-full"
                              name="shipping"
                              value={shippingCompany}
                              disabled={parcelInfo?.shippingMethods?.name ? true : false}
                              onChange={(e) => setShippingCompany(e.target.value)}
                            />
                          </li>
                          <li className="mt-2">
                            <label htmlFor="tracking">{localeText("trackingObject")}:</label>
                          </li>
                          <li>
                            <InputText
                              className="mt-2 w-full"
                              name="tracking"
                              value={trackingObject}
                              disabled={parcelInfo?.trackingNumber ? true : false}
                              onChange={(e) => setTrackingObject(e.target.value)}
                            />
                          </li>
                        </ul>
                      </>
                    )}
                  </>
                )}
              </>
            );
          }}
          header={localeText("sendOrder")}
          icon="pi pi-exclamation-triangle"
          accept={() => {
            sendOrder(
              localStorage.getItem("__extraconomy_username"),
              currentOrder.id,
              shippingCompany,
              trackingObject,
            );
          }}
          reject={() => setConfirmSendVisible(false)}
          acceptLabel={localeText("confirm")}
          rejectLabel={localeText("cancel")}
        />

        {props.admin && (
          <div className="flex align-items-center gap-2 flex-wrap mb-3">
            <InputText
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              placeholder={localeText("searchByStoreName")}
              className="p-inputtext-sm border-round-md p-no-outline"
              style={{ minWidth: "12rem" }}
            />
            <Button
              icon="pi pi-search"
              rounded
              text
              onClick={filterByStoreName}
              className="p-button-outlined"
              style={{ color: "var(--theme-primary)" }}
            />
          </div>
        )}

        {isPurchasesView && (
          <PendingReceiptReminder orders={orders} loading={loading} receiveOrder={receiveOrder} />
        )}

        <div className="financial-transactions-table">
          <OrdersTable
            admin={props.admin}
            sales={props.sales}
            orders={orders}
            handleCurrency={handleCurrency}
            loading={loading}
            setCurrency={setCurrency}
            setCurrentOrder={setCurrentOrder}
            setOrderId_={setOrderId_}
            setDisplayRateVisible={setDisplayRateVisible}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Orders;
